import { BlogPostGrid, FeaturedBlogPostsBlock } from "components/Blog";
import Layout from "components/Layout";
import { PageNavBar } from "components/PageNavBar";
import { readCMSDataClientSide, readCMSDataServerSide } from "helpers/api";
import { useQuery } from "helpers/query";
import type { BlogPostTile, DRFPaginatedQuery } from "interfaces";
import type { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import {
    EmtekPageProps,
    InferGetWagtailServerSidePropsType,
} from "pages/[[...slug]]";
import { useRef } from "react";

import { EmtekClientProvider } from "../../queryClient";
import { MultiLayoutCmsData, MultiLayoutSubPage } from "./MultiLayoutSubPage";
import { Toolbox, ToolboxBlock } from "./Toolbox";

export type PageProps = EmtekPageProps<
    MultiLayoutCmsData<ToolboxBlock | FeaturedBlogPostsBlock>
>;

type Props = InferGetWagtailServerSidePropsType<typeof fetchData>;

export default function BlogLandingPageWrapper(props: Props) {
    return (
        <EmtekClientProvider>
            <BlogLandingPage {...props} />
        </EmtekClientProvider>
    );
}

function BlogLandingPage({ wagtail, menus, tiles: initialTiles }: Props) {
    const router = useRouter();
    const mainBlogGrid = useRef<HTMLElement>(null);

    const { data: tiles } = useQuery<DRFPaginatedQuery<BlogPostTile>>({
        namespaceID: menus[0].site,
        queryKey: ["tiles", wagtail.id, router.query],
        queryFn: () => {
            return readCMSDataClientSide(`/api/blogtiles/`, {
                search: new URLSearchParams(window.location.search),
            });
        },
        initialData: initialTiles,
    });

    if (!tiles)
        throw new Error(
            "Tile data should always be present, either from initial data or aditional fetched data.",
        );

    return (
        <Layout metadata={wagtail} menus={menus}>
            <MultiLayoutSubPage
                body={wagtail.body}
                split_layout_nav={wagtail.split_layout_nav}
            >
                {wagtail.split_layout_body.map((block, i) => {
                    switch (block.type) {
                        case "featured_blog_posts":
                            const tiles = block.value.posts.map((post) => {
                                return post.value;
                            });
                            return (
                                <BlogPostGrid
                                    key={block.id}
                                    tiles={tiles}
                                    isFeatureGrid={true}
                                    title={block.value.title}
                                />
                            );
                        default:
                            return (
                                <Toolbox
                                    key={block.id}
                                    block={block}
                                    isFirstBlock={
                                        wagtail.body.length === 0 && i === 0
                                    }
                                />
                            );
                    }
                })}
            </MultiLayoutSubPage>
            <BlogPostGrid
                tiles={tiles.results}
                title={"The Latest"}
                ref={mainBlogGrid}
            />
            <PageNavBar {...tiles} padBottom={true} />
        </Layout>
    );
}

export async function fetchData(
    context: GetServerSidePropsContext,
    wagtailPageProps: PageProps,
    apiResponse: Response,
) {
    const query = new URL(
        context.resolvedUrl,
        `https://${context.req.headers.host}`,
    ).searchParams;
    const tiles = (await readCMSDataServerSide(`/api/blogtiles/`, {
        context: context,
        search: query,
    })) as DRFPaginatedQuery<BlogPostTile>;

    return {
        props: {
            ...wagtailPageProps,
            tiles,
        },
        apiResponse: apiResponse,
    };
}
